import {
  CategoriesService,
  type PostCategoryRequestAdmin,
  type PutCategoryRequestAdmin
} from '@/backend/market'
import { CategoryRepository } from '../../domain/repositories/CategoryRepository'
import { CategoryModel } from '../models/CategoryModel'
import { CategoryFilterModel } from '../models/CategoryFilterModel'
import type { CategoryFilterEntity } from '../../domain/entities/CategoryFilterEntity'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import { CategoryMiniModel } from '../models/CategoryMiniModel'
import type { CategoryEntity } from '../../domain/entities/CategoryEntity'
import type { CreateUpdateCategoryEntity } from '../../domain/entities/CreateUpdateCategoryEntity'
import { CreateUpdateCategoryModel } from '../models/CreateUpdateCategorymodel'
import type { CategoryMiniEntity } from '../../domain/entities/CategoryMiniEntity'
import { AllCategoriesFilterModel } from '../models/AllCategoriesFilterModel'
import type { AllCategoriesFilterEntity } from '../../domain/entities/AllCategoriesFilterEntity'
import type { AllCategoriesMiniEntity } from '../../domain/entities/AllCategoriesMiniEntity'
import { AllCategoriesMiniModel } from '../models/AllCategoriesMiniModel'

export class CategoryRepositoryImpl implements CategoryRepository {
  async getCategories(
    filterData: CategoryFilterEntity
  ): Promise<PaginateEntity<CategoryMiniModel[]> | { error: true }> {
    const categoryFilterModel = new CategoryFilterModel(filterData)
    try {
      const response = await CategoriesService.getCategoriesAdmin(categoryFilterModel)

      const categoryMiniModels = response.items?.map(
        (item) => new CategoryMiniModel(item as CategoryMiniEntity)
      )
      const categoryResponse = new PaginateEntity<CategoryMiniModel[]>(response, categoryMiniModels)

      return categoryResponse
    } catch {
      return { error: true }
    }
  }

  async getAllCategories(
    filterData: AllCategoriesFilterEntity
  ): Promise<AllCategoriesMiniEntity[] | { error: true }> {
    try {
      const allCategoriesFilterModel = new AllCategoriesFilterModel(filterData)
      const response = await CategoriesService.getAllCategoriesAdmin(allCategoriesFilterModel)
      return response.categories?.map((item) => new AllCategoriesMiniModel(item)) || []
    } catch {
      return { error: true }
    }
  }

  async getCategoryById(id: number): Promise<CategoryEntity | { error: true }> {
    try {
      const response = await CategoriesService.getCategoryAdmin({ id })
      const parentCategory = response.parentId
        ? await this.getCategories({ id: response.parentId })
        : null
      const categoryModel = new CategoryModel(response)
      return {
        ...categoryModel,
        parentCategory: parentCategory
          ? (parentCategory as { items: CategoryMiniEntity[] })?.items[0]
          : undefined
      }
    } catch {
      return { error: true }
    }
  }

  async createCategory(
    categoryData: CreateUpdateCategoryEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateCategoryModel = new CreateUpdateCategoryModel(categoryData)
    try {
      await CategoriesService.postCategoryAdmin({
        requestBody: createUpdateCategoryModel as PostCategoryRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async updateCategory(
    id: number,
    categoryData: CreateUpdateCategoryEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateCategoryModel = new CreateUpdateCategoryModel(categoryData)
    try {
      await CategoriesService.putCategoryAdmin({
        id,
        requestBody: createUpdateCategoryModel as PutCategoryRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async reOrderCategory(
    id: number,
    newOrder?: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await CategoriesService.putCategoryOrderAdmin({
        id,
        requestBody: {
          order: newOrder === undefined ? null : newOrder
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async deleteCategory(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await CategoriesService.deleteCategoryAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
